import { CAB_TO_TICKETS, TICKETS_TO_CAB, type SyncApplyResult } from '../../services/requirementSyncApi';

export const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/** Why the CAB can't be edited while a sync review waits (the backend refuses with the same words). */
export const SYNC_REVIEW_WAITING = 'A sync review is waiting for a decision. Review it first.';

/** Why a ticket in that review can't be edited or deleted (the backend refuses with the same words). */
export const syncTicketLocked = (ids: string[]) => (ids.length === 1
  ? `${ids[0]} is part of a sync review that is waiting for a decision. Review it first.`
  : `${ids.join(', ')} are part of a sync review that is waiting for a decision. Review it first.`);

// Ticket edits a tickets->CAB apply leaves pending (the run could not work them out), as a sentence; '' if none.
const stillPending = (r: SyncApplyResult) => (r.stillPending.length
  ? ` ${plural(r.stillPending.length, 'ticket')} (${r.stillPending.join(', ')}) still pending; update the CAB again to work ${r.stillPending.length === 1 ? 'it' : 'them'} out.`
  : '');

// What an apply did, per direction. Shared so the CAB page's inline review reports a save as the banner does.
const APPLIED: Record<string, (r: SyncApplyResult) => string> = {
  [TICKETS_TO_CAB]: r => {
    const done = r.applied ? `${plural(r.applied, 'CAB change')} applied` : 'No CAB changes applied';
    return `${done}.${stillPending(r)}`;
  },
  [CAB_TO_TICKETS]: r => {
    const done = r.applied ? `${plural(r.applied, 'ticket change')} applied` : 'No ticket changes applied';
    const n = r.skipped.length;
    const skipped = n
      ? `, ${n} skipped because ${n === 1 ? 'the ticket' : 'the tickets'} changed meanwhile; ${n === 1 ? 'it' : 'they'} will be checked again`
      : '';
    return `${done}${skipped}.`;
  },
};

/** An apply's notice is a success only when it changed something; otherwise it is said neutrally. */
export const syncAppliedAnything = (r: SyncApplyResult): boolean => r.applied > 0;

/** The notice after an apply, in the words of the rule that ran it ("tickets->cab" or "cab->tickets"). */
export function syncAppliedText(ruleId: string, r: SyncApplyResult): string {
  return (APPLIED[ruleId] ?? APPLIED[CAB_TO_TICKETS])(r);
}

/** The notice after Discard, which rejects every proposed change: nothing changes, and the edits count as handled. */
export function syncDiscardedText(ruleId: string, r: SyncApplyResult): string {
  return ruleId === TICKETS_TO_CAB
    ? `All changes discarded. The CAB stays as it is.${stillPending(r)}`
    : 'All changes discarded. The tickets stay as they are.';
}
